import { Colors, IcFolder, IcPlayV2, Text } from 'basic-ui-lib';
import BaseModal from 'basic-ui-lib/components/Modal/BaseModal';
import { IFolder, IMediaItem, MediaType } from 'modules/media/interfaces/api';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import IcCheckBox from 'basic-ui-lib/components/Checkbox/assets/IcCheckBox.svg';
import IcCheckBoxSelected from 'basic-ui-lib/components/Checkbox/assets/IcCheckboxSelected.svg';
import { formatDuration } from 'utils/time';
import useGetAllFoldersInStore from 'modules/media/services/hooks/useGetAllFolderInStore';
import mediaApis from 'modules/media/services/apis';
import toast from 'utils/toast';

export interface MoveToFolderModalRef {
  open: (media: IMediaItem) => void;
  close: () => void;
}

interface MoveToFolderModalProps {
  storeId?: string;
  onSuccess: (currentPath: string) => void;
  currentPath: string;
}

const MoveToFolderModal = forwardRef<
  MoveToFolderModalRef,
  MoveToFolderModalProps
>(({ storeId, onSuccess, currentPath }, ref) => {
  const [visible, setVisible] = useState(false);
  const [media, setMedia] = useState<IMediaItem | null>(null);
  const [data] = useGetAllFoldersInStore(storeId || '');
  const [selectedFolderPath, setSelectedFolderPath] = useState<string | undefined>(
    undefined,
  );

  useImperativeHandle(ref, () => ({
    open: (selectedMedia: IMediaItem) => {
      setMedia(selectedMedia);
      setSelectedFolderPath(undefined);
      setVisible(true);
    },

    close: () => {
      setVisible(false);
    },
  }));

  const handleToggleFolder = (fullPath: string) => {
    setSelectedFolderPath((prev) => (prev === fullPath ? undefined : fullPath));
  };

  const handleConfirm = async () => {
    if (!media || !selectedFolderPath || !storeId) {
      return;
    }

    try {
      const res = await mediaApis.moveToFolder(
        storeId || '',
        media.id,
        selectedFolderPath,
      );

      if (res?.data?.data) {
        onSuccess(currentPath);
        toast.success('Moved successfully');
      }
    } catch (error) {
      toast.error('Failed to move');
    }

    setVisible(false);
  };

  const filteredData = data?.filter((folder) => folder.id !== media?.id);

  if (!visible || !media) {
    return null;
  }

  return (
    <BaseModal
      title='Move to Folder'
      isOpen={visible}
      width={700}
      onClose={() => setVisible(false)}
      okText='OK'
      onOk={handleConfirm}
      disabled={!selectedFolderPath}
    >
      <Container>
        {/* Selected media */}
        <MediaCard>
          <ThumbnailWrapper>
            {media.type === MediaType.FOLDER ? (
              <IcFolder />
            ) : (
              <>
                <Thumbnail src={media.type === MediaType.VIDEO ? media.thumbnail : media.url} alt={media.name} />

                {media.type === MediaType.VIDEO && (
                  <>
                    <PlayButton>
                      <IcPlayV2 />
                    </PlayButton>

                    <Duration>{formatDuration(media.duration ?? 0)}</Duration>
                  </>
                )}
              </>
            )}
          </ThumbnailWrapper>

          <MediaInfo>
            <Text title_h9 semibold>
              {media.name}
            </Text>

            <Text
              caption_2
              medium
              color='neutral.text.3'
              style={{ textTransform: 'capitalize', letterSpacing: '0.5px' }}
            >
              {media.type.toLocaleLowerCase()} · {media.size}
            </Text>
          </MediaInfo>
        </MediaCard>

        {/* Section title */}
        <Text content_2 medium color='neutral.text.3' left>
          SELECT DESTINATION FOLDER
        </Text>

        {/* Search */}
        {/* <InputSearch
          value={search}
          onChange={(value) => setSearch(value)}
          placeholder='Search stores'
        /> */}

        {/* Stores */}
        <StoreList>
          {filteredData?.map((folder: IFolder) => {
            const checked = selectedFolderPath === folder.fullPath;

            return (
              <StoreItem
                key={folder.id}
                checked={checked}
                onClick={() => handleToggleFolder(folder.fullPath)}
              >
                <StoreLeft>
                  <StoreIconWrapper>
                    <IcFolder />
                  </StoreIconWrapper>

                  <StoreInfo>
                    <Text content_2 medium left>
                      {folder.name}
                    </Text>

                    {/* <Text caption_3 color='neutral.text.3' left>
                      {folder.totalScreen} items
                    </Text> */}
                  </StoreInfo>
                </StoreLeft>

                {checked ? (
                  <img src={IcCheckBoxSelected} alt='Checked' />
                ) : (
                  <img src={IcCheckBox} alt='Unchecked' />
                )}
              </StoreItem>
            );
          })}

          {!filteredData?.length && <EmptyState>No stores found</EmptyState>}
        </StoreList>
      </Container>
    </BaseModal>
  );
});

MoveToFolderModal.displayName = 'MoveToFolderModal';

export default MoveToFolderModal;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 0 24px;
`;

const MediaCard = styled.div`
  display: flex;
  gap: 16px;
  padding: 12px;
  background: ${Colors.neutral.bg[2]};
  border: 1px solid ${Colors.neutral.bg[3]};
  border-radius: 12px;
`;

const ThumbnailWrapper = styled.div`
  position: relative;

  flex: 0 0 175px;
  aspect-ratio: 16 / 9;
  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  border-radius: 9px;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Duration = styled.span`
  position: absolute;
  right: 5px;
  bottom: 5px;
  padding: 2px 4px;
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 2px;
  font-size: 10px;
`;

const MediaInfo = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
`;

const StoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  padding-right: 12px;
  max-height: 280px;

  overflow-y: auto;

  scrollbar-width: thin;
`;

const StoreItem = styled.div<{ checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border: 1px solid
    ${({ checked }) => (checked ? '#8ddcb8' : `${Colors.neutral.bg[3]}`)};
  border-radius: 8px;
  background: ${({ checked }) =>
    checked ? '#f0fcf6' : `${Colors.neutral.bg[2]}`};
  cursor: pointer;
  transition: 0.15s ease;
  &:hover {
    border-color: #aeb7c5;
  }
`;

const StoreLeft = styled.div`
  display: flex;
  align-items: center;

  min-width: 0;
`;

const StoreIconWrapper = styled.div`
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  border: 1px solid ${Colors.neutral.bg[3]};
  border-radius: 8px;

  background: #fff;
`;

const StoreInfo = styled.div`
  min-width: 0;
  margin-left: 16px;
`;

const EmptyState = styled.div`
  padding: 30px 0;

  text-align: center;

  color: #8b95a6;

  font-size: 14px;
`;
